import React, { useState, useEffect } from 'react';
import { Settings, CheckCircle, AlertCircle, Users, Calendar, Link as LinkIcon, Key, Eye, EyeOff, Power, ExternalLink } from 'lucide-react';
import './GoogleOAuthConfig.css';

// API helper
const api = (path, opts={}) => {
  const base = '/api';
  return fetch(base + path, {
    credentials: 'include',
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers||{})
    }
  });
};

function GoogleOAuthConfig() {
  const [activeTab, setActiveTab] = useState('config');
  const [config, setConfig] = useState({
    clientId: '',
    clientSecret: '',
    redirectUri: '',
    isActive: true
  });
  const [originalConfig, setOriginalConfig] = useState(null);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showClientSecret, setShowClientSecret] = useState(false);

  // Stats state
  const [stats, setStats] = useState({
    totalMerchants: 0,
    connectedMerchants: 0,
    connectionRate: 0,
    isConfigured: false,
    isActive: false
  });

  // Connected merchants state
  const [connectedMerchants, setConnectedMerchants] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(false);
  const [merchantsPage, setMerchantsPage] = useState(1);
  const [merchantsTotal, setMerchantsTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // Load configuration
  const loadConfig = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api('/admin/google-oauth/config');

      if (response.ok) {
        const data = await response.json();
        console.log('[Google OAuth] Config loaded:', data.configured);

        if (data.configured && data.config) {
          setConfigured(true);
          setOriginalConfig(data.config);
          setConfig({
            clientId: data.config.clientId || '',
            clientSecret: '', // Don't populate secret from API
            redirectUri: data.config.redirectUri || '',
            isActive: data.config.isActive !== undefined ? data.config.isActive : true
          });
        } else {
          setConfigured(false);
          // Set default redirect URI
          setConfig(prev => ({
            ...prev,
            redirectUri: `${window.location.origin}/api/merchant/calendar/oauth/callback`
          }));
        }
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to load configuration');
      }
    } catch (e) {
      console.error('[Google OAuth] Error loading config:', e);
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  // Load stats
  const loadStats = async () => {
    try {
      const response = await api('/admin/google-oauth/stats');

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats || stats);
      }
    } catch (e) {
      console.error('[Google OAuth] Error loading stats:', e);
    }
  };

  // Load connected merchants
  const loadConnectedMerchants = async (page = 1, search = '') => {
    try {
      setMerchantsLoading(true);

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(search && { search })
      });

      const response = await api(`/admin/google-oauth/connected-merchants?${queryParams}`);

      if (response.ok) {
        const data = await response.json();
        setConnectedMerchants(data.data.merchants || []);
        setMerchantsTotal(data.data.pagination.total || 0);
        setMerchantsPage(page);
      }
    } catch (e) {
      console.error('[Google OAuth] Error loading merchants:', e);
    } finally {
      setMerchantsLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
    loadStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'merchants') {
      loadConnectedMerchants(1, searchTerm);
    }
  }, [activeTab]);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Save configuration
  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Validation
      if (!config.clientId || !config.clientSecret || !config.redirectUri) {
        setError('All fields are required');
        setSaving(false);
        return;
      }

      if (!config.clientId.endsWith('.apps.googleusercontent.com')) {
        setError('Client ID must end with .apps.googleusercontent.com');
        setSaving(false);
        return;
      }

      const response = await api('/admin/google-oauth/config', {
        method: 'PUT',
        body: JSON.stringify(config)
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess('OAuth configuration saved successfully!');
        setConfigured(true);
        loadConfig();
        loadStats();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save configuration');
      }
    } catch (e) {
      console.error('[Google OAuth] Error saving config:', e);
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Test connection
  const handleTestConnection = async () => {
    setTesting(true);
    setError('');
    setSuccess('');

    try {
      const response = await api('/admin/google-oauth/test', {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess('OAuth configuration is valid and working! ✓');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'OAuth configuration test failed');
      }
    } catch (e) {
      console.error('[Google OAuth] Error testing config:', e);
      setError('Failed to test configuration');
    } finally {
      setTesting(false);
    }
  };

  // Toggle OAuth active status
  const handleToggleActive = async () => {
    try {
      const newStatus = !config.isActive;

      const response = await api('/admin/google-oauth/toggle', {
        method: 'PATCH',
        body: JSON.stringify({ isActive: newStatus })
      });

      if (response.ok) {
        setConfig(prev => ({ ...prev, isActive: newStatus }));
        setSuccess(`OAuth integration ${newStatus ? 'enabled' : 'disabled'} successfully!`);
        loadStats();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to toggle OAuth status');
      }
    } catch (e) {
      console.error('[Google OAuth] Error toggling status:', e);
      setError('Failed to toggle OAuth status');
    }
  };

  // Disconnect merchant
  const handleDisconnectMerchant = async (merchantId, merchantEmail) => {
    if (!window.confirm(`Are you sure you want to disconnect calendar for ${merchantEmail}?`)) {
      return;
    }

    try {
      const response = await api(`/admin/google-oauth/merchant/${merchantId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess('Merchant calendar disconnected successfully!');
        loadConnectedMerchants(merchantsPage, searchTerm);
        loadStats();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to disconnect merchant calendar');
      }
    } catch (e) {
      console.error('[Google OAuth] Error disconnecting merchant:', e);
      setError('Failed to disconnect merchant calendar');
    }
  };

  if (loading) {
    return (
      <div className="google-oauth-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading OAuth configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="google-oauth-container">
      {/* Header */}
      <div className="oauth-header">
        <div className="header-icon-wrapper">
          <Calendar className="header-icon" />
        </div>
        <div className="header-content">
          <h1 className="page-title">Google Calendar OAuth Setup</h1>
          <p className="page-subtitle">Configure platform-wide Google OAuth for merchant calendar integrations</p>
        </div>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="alert alert-error">
          <AlertCircle className="alert-icon" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <CheckCircle className="alert-icon" />
          <span>{success}</span>
        </div>
      )}

      {/* Status Card */}
      <div className="status-card">
        <div className="status-grid">
          <div className="status-item">
            <Settings className="status-icon" />
            <div>
              <div className="status-label">Configuration Status</div>
              <div className={`status-value ${configured ? 'text-success' : 'text-warning'}`}>
                {configured ? 'Configured' : 'Not Configured'}
              </div>
            </div>
          </div>

          <div className="status-item">
            <Power className="status-icon" />
            <div>
              <div className="status-label">Integration Status</div>
              <div className={`status-value ${stats.isActive ? 'text-success' : 'text-error'}`}>
                {stats.isActive ? 'Active' : 'Inactive'}
              </div>
            </div>
          </div>

          <div className="status-item">
            <Users className="status-icon" />
            <div>
              <div className="status-label">Connected Merchants</div>
              <div className="status-value">{stats.connectedMerchants} / {stats.totalMerchants}</div>
            </div>
          </div>

          <div className="status-item">
            <CheckCircle className="status-icon" />
            <div>
              <div className="status-label">Connection Rate</div>
              <div className="status-value">{stats.connectionRate}%</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button
          className={`tab-btn ${activeTab === 'config' ? 'active' : ''}`}
          onClick={() => setActiveTab('config')}
        >
          <Settings size={18} />
          Configuration
        </button>
        <button
          className={`tab-btn ${activeTab === 'merchants' ? 'active' : ''}`}
          onClick={() => setActiveTab('merchants')}
        >
          <Users size={18} />
          Connected Merchants ({stats.connectedMerchants})
        </button>
        <button
          className={`tab-btn ${activeTab === 'instructions' ? 'active' : ''}`}
          onClick={() => setActiveTab('instructions')}
        >
          <LinkIcon size={18} />
          Setup Instructions
        </button>
      </div>

      {/* Configuration Tab */}
      {activeTab === 'config' && (
        <div className="config-card">
          <form onSubmit={handleSaveConfig} className="oauth-form">
            <div className="form-section">
              <h3 className="section-title">Google Cloud OAuth Credentials</h3>
              <p className="section-description">
                Enter the OAuth 2.0 credentials from your Google Cloud project
              </p>

              <div className="form-group">
                <label>Client ID</label>
                <input
                  type="text"
                  name="clientId"
                  value={config.clientId}
                  onChange={handleInputChange}
                  placeholder="123456789-abcdefg.apps.googleusercontent.com"
                  className="form-input"
                  required
                />
                <span className="input-hint">Must end with .apps.googleusercontent.com</span>
              </div>

              <div className="form-group">
                <label>Client Secret</label>
                <div className="password-input-wrapper">
                  <input
                    type={showClientSecret ? 'text' : 'password'}
                    name="clientSecret"
                    value={config.clientSecret}
                    onChange={handleInputChange}
                    placeholder={configured ? '••••••••••••••••••••' : 'Enter client secret'}
                    className="form-input"
                    required={!configured}
                  />
                  <button
                    type="button"
                    onClick={() => setShowClientSecret(!showClientSecret)}
                    className="show-password-btn"
                  >
                    {showClientSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <span className="input-hint">
                  {configured ? 'Leave empty to keep current secret' : 'Your Google OAuth client secret'}
                </span>
              </div>

              <div className="form-group">
                <label>Redirect URI</label>
                <input
                  type="url"
                  name="redirectUri"
                  value={config.redirectUri}
                  onChange={handleInputChange}
                  placeholder={`${window.location.origin}/api/merchant/calendar/oauth/callback`}
                  className="form-input"
                  required
                />
                <span className="input-hint">This must be added to your Google Cloud Console authorized redirect URIs</span>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>

              {configured && (
                <>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleTestConnection}
                    disabled={testing}
                  >
                    {testing ? 'Testing...' : 'Test Connection'}
                  </button>

                  <button
                    type="button"
                    className={config.isActive ? 'btn-danger' : 'btn-success'}
                    onClick={handleToggleActive}
                  >
                    <Power size={18} />
                    {config.isActive ? 'Disable Integration' : 'Enable Integration'}
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Connected Merchants Tab */}
      {activeTab === 'merchants' && (
        <div className="merchants-card">
          <div className="merchants-header">
            <h3>Connected Merchants</h3>
            <input
              type="search"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && loadConnectedMerchants(1, searchTerm)}
              className="search-input"
            />
          </div>

          {merchantsLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading merchants...</p>
            </div>
          ) : connectedMerchants.length === 0 ? (
            <div className="empty-state">
              <Users size={48} className="empty-icon" />
              <p>No merchants have connected their calendars yet</p>
            </div>
          ) : (
            <div className="merchants-table-wrapper">
              <table className="merchants-table">
                <thead>
                  <tr>
                    <th>Merchant</th>
                    <th>Connected Email</th>
                    <th>Calendar ID</th>
                    <th>Connected At</th>
                    <th>Calendars</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {connectedMerchants.map(merchant => (
                    <tr key={merchant.id}>
                      <td>
                        <div className="merchant-info">
                          <div className="merchant-name">{merchant.username}</div>
                          <div className="merchant-email">{merchant.email}</div>
                        </div>
                      </td>
                      <td>{merchant.connectedEmail}</td>
                      <td>
                        <code className="calendar-id">{merchant.calendarId}</code>
                      </td>
                      <td>{new Date(merchant.connectedAt).toLocaleDateString()}</td>
                      <td>
                        <span className="badge">{merchant.calendarsCount} calendars</span>
                      </td>
                      <td>
                        <button
                          onClick={() => handleDisconnectMerchant(merchant.id, merchant.email)}
                          className="btn-disconnect"
                        >
                          Disconnect
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {merchantsTotal > 20 && (
                <div className="pagination">
                  <button
                    onClick={() => loadConnectedMerchants(merchantsPage - 1, searchTerm)}
                    disabled={merchantsPage === 1}
                    className="btn-pagination"
                  >
                    Previous
                  </button>
                  <span>Page {merchantsPage} of {Math.ceil(merchantsTotal / 20)}</span>
                  <button
                    onClick={() => loadConnectedMerchants(merchantsPage + 1, searchTerm)}
                    disabled={merchantsPage >= Math.ceil(merchantsTotal / 20)}
                    className="btn-pagination"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Setup Instructions Tab */}
      {activeTab === 'instructions' && (
        <div className="instructions-card">
          <h3 className="section-title">Google Cloud Project Setup</h3>

          <div className="instructions-content">
            <div className="instruction-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Create Google Cloud Project</h4>
                <p>Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="external-link">
                  Google Cloud Console <ExternalLink size={14} />
                </a> and create a new project or select an existing one.</p>
              </div>
            </div>

            <div className="instruction-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>Enable Google Calendar API</h4>
                <p>Navigate to "APIs & Services" → "Library" and search for "Google Calendar API". Click on it and enable it for your project.</p>
              </div>
            </div>

            <div className="instruction-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>Create OAuth 2.0 Credentials</h4>
                <p>Go to "APIs & Services" → "Credentials" → "Create Credentials" → "OAuth client ID"</p>
                <ul>
                  <li>Application type: <strong>Web application</strong></li>
                  <li>Name: Your platform name (e.g., "My Platform Calendar Integration")</li>
                </ul>
              </div>
            </div>

            <div className="instruction-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h4>Configure Authorized Redirect URIs</h4>
                <p>Add the following redirect URI to your OAuth client:</p>
                <div className="code-block">
                  <code>{config.redirectUri || `${window.location.origin}/api/merchant/calendar/oauth/callback`}</code>
                  <button
                    onClick={() => navigator.clipboard.writeText(config.redirectUri || `${window.location.origin}/api/merchant/calendar/oauth/callback`)}
                    className="copy-btn"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>

            <div className="instruction-step">
              <div className="step-number">5</div>
              <div className="step-content">
                <h4>Get Your Credentials</h4>
                <p>After creating the OAuth client, you'll receive:</p>
                <ul>
                  <li><strong>Client ID</strong>: A long string ending with .apps.googleusercontent.com</li>
                  <li><strong>Client Secret</strong>: A secret key for your application</li>
                </ul>
                <p>Copy both and paste them into the Configuration tab above.</p>
              </div>
            </div>

            <div className="instruction-step">
              <div className="step-number">6</div>
              <div className="step-content">
                <h4>Configure OAuth Consent Screen (if not done)</h4>
                <p>Go to "OAuth consent screen" and configure:</p>
                <ul>
                  <li>User Type: External (or Internal for Google Workspace)</li>
                  <li>App name, support email, and developer contact</li>
                  <li>Add scopes: <code>calendar.readonly</code> and <code>calendar.events</code></li>
                </ul>
              </div>
            </div>

            <div className="instruction-step">
              <div className="step-number">7</div>
              <div className="step-content">
                <h4>Test the Integration</h4>
                <p>After saving your configuration:</p>
                <ol>
                  <li>Click "Test Connection" to verify your setup</li>
                  <li>Enable the integration using the toggle</li>
                  <li>Merchants can now connect their calendars!</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="help-box">
            <AlertCircle className="help-icon" />
            <div>
              <h4>Need Help?</h4>
              <p>For detailed Google OAuth setup instructions, visit the <a href="https://developers.google.com/calendar/api/guides/overview" target="_blank" rel="noopener noreferrer">Google Calendar API Documentation</a></p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GoogleOAuthConfig;
